import { Link, useOutletContext } from "react-router";
import { Fragment, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { CountChip } from "@/components/count-chip";
import { CountryChips } from "@/components/table-chips";
import { Icon } from "@/components/icons";
import { PaginationFooter } from "@/components/pagination-footer";
import { Tag } from "@/components/tag";
import { Tooltip } from "@/components/tooltip";
import { formatNumber } from "@/app/(app)/_lib/format";
import type { AppOutletContext } from "@/src/layouts/AppLayout";
import {
  listOpenAlerts,
  getNewMatchesPreview,
  type MonitoringEntry,
} from "@/src/services/monitoring_service";
import { TableCardSkeleton } from "./skeletons";

const ROWS_PER_PAGE = 10;

const ROW_UNDERSTROKE_BG =
  "repeating-linear-gradient(to bottom, transparent 0, transparent 47px, var(--color-grey-100) 47px, var(--color-grey-100) 48px)";

type AlertColumnId = "name" | "identifier" | "country" | "matches" | "new";

type AlertColumn = {
  id: AlertColumnId;
  header: string;
  /** Grid-template-columns fragment, e.g. "minmax(120px,1fr)" or "80px". */
  width: string;
  cell: (entry: MonitoringEntry) => ReactNode;
};

const ROW_BORDER = "border-b border-grey-100";

const ALERT_COLUMNS: AlertColumn[] = [
  {
    id: "name",
    header: "Name",
    width: "minmax(120px,1fr)",
    cell: (entry) => (
      <span className="flex h-12 min-w-0 items-center px-2 text-small text-grey-900">
        {entry.name ? (
          <Tooltip description={entry.name} width="auto" wrapperClassName="min-w-0 truncate">
            {entry.name}
          </Tooltip>
        ) : (
          <span className="text-grey-500">—</span>
        )}
      </span>
    ),
  },
  {
    id: "identifier",
    header: "Identifier",
    width: "120px",
    cell: (entry) => (
      <span className="flex h-12 min-w-0 items-center px-2 text-small text-grey-900">
        {entry.identifier ? (
          <Tooltip description={entry.identifier} width="auto" wrapperClassName="min-w-0 truncate">
            {entry.identifier}
          </Tooltip>
        ) : (
          <span className="text-grey-500">—</span>
        )}
      </span>
    ),
  },
  {
    id: "country",
    header: "Country",
    width: "88px",
    cell: (entry) => (
      <span className="flex h-12 items-center px-2 text-small text-grey-900">
        <CountryChips countries={entry.countries ?? (entry.country ? [entry.country] : [])} />
      </span>
    ),
  },
  {
    id: "matches",
    header: "Total",
    width: "80px",
    cell: (entry) => (
      <span className="flex h-12 items-center px-2">
        <CountChip count={entry.matches} />
      </span>
    ),
  },
  {
    id: "new",
    header: "New",
    width: "80px",
    cell: (entry) => (
      <span className="flex h-12 items-center px-2">
        <CountChip count={entry.newCount} />
      </span>
    ),
  },
];

export function ActiveMonitoringAlertsCard({
  empty = false,
  entries = null,
  count,
  page = 1,
  onPageChange,
  pageFetching = false,
  newCount,
}: {
  empty?: boolean;
  entries?: MonitoringEntry[] | null;
  count?: number;
  page?: number;
  onPageChange?: (page: number) => void;
  pageFetching?: boolean;
  newCount?: number;
} = {}) {
  const rows = empty ? [] : (entries ?? []);
  const total = count ?? rows.length;
  const isEmpty = total === 0;
  const totalNew = newCount ?? rows.reduce((sum, e) => sum + e.newCount, 0);
  const pageCount = Math.max(1, Math.ceil(total / ROWS_PER_PAGE));
  const gridTemplate = ALERT_COLUMNS.map((c) => c.width).join(" ");
  const wrapperStyle: CSSProperties = isEmpty
    ? { minHeight: 36 + ROWS_PER_PAGE * 48 }
    : {
        backgroundImage: ROW_UNDERSTROKE_BG,
        backgroundPosition: "0 36px",
        minHeight: 36 + ROWS_PER_PAGE * 48,
      };

  return (
    <section className="card flex flex-1 flex-col gap-4">
      <header className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <h3 className="text-body font-semibold text-black">Active monitoring alerts</h3>
          <p className="text-small text-grey-600">
            Real-time status changes across monitored names
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {totalNew > 0 && (
            <Tag size="sm" tone="negative">
              {formatNumber(totalNew)} new
            </Tag>
          )}
          {!isEmpty && (
            <Link
              to="/monitoring-portal"
              className="-my-3.5 inline-flex items-center gap-1 py-3.5 text-tiny font-medium uppercase tracking-[0.04em] text-green-700 hover:text-green-600"
            >
              View all
              <Icon name="arrow_outward" size={20} />
            </Link>
          )}
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col overflow-x-auto rounded-[4px] border border-grey-200 bg-white shadow-flat">
        <div
          className={`flex flex-col transition-opacity ${pageFetching ? "pointer-events-none opacity-50" : ""}`}
          style={wrapperStyle}
        >
          <div className="grid w-full" style={{ gridTemplateColumns: gridTemplate }}>
            {ALERT_COLUMNS.map((col) => (
              <HeaderCell key={col.id}>{col.header}</HeaderCell>
            ))}
          </div>
          {rows.map((entry) => (
            <AlertRow key={entry.id} entry={entry} gridTemplate={gridTemplate} />
          ))}
          {isEmpty && (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4 py-10 text-center">
              <span className="flex size-10 items-center justify-center rounded-full bg-grey-100 text-grey-500">
                <Icon name="notifications_off" size={20} />
              </span>
              <div className="flex flex-col gap-1">
                <p className="text-small font-medium text-grey-900">No active alerts</p>
                <p className="text-small text-grey-600">
                  Add names to monitor to start receiving alerts here.
                </p>
              </div>
            </div>
          )}
        </div>
        {pageCount > 1 && (
          <PaginationFooter
            compact
            total={total}
            individuals={0}
            organizations={0}
            others={0}
            page={Math.min(page, pageCount)}
            pageCount={pageCount}
            onPageChange={onPageChange ?? (() => {})}
          />
        )}
      </div>
    </section>
  );
}

export function ActiveMonitoringAlertsSection({
  monitoring,
  previewMode,
  empty,
  skeletonRows = 5,
}: {
  monitoring: boolean;
  previewMode: boolean;
  empty: boolean;
  skeletonRows?: number;
}) {
  const ctx = useOutletContext<AppOutletContext | null>();
  const infoResolved = ctx?.infoResolved ?? false;
  const liveMonitoring = monitoring && !previewMode;

  const [page, setPage] = useState(1);
  const [entries, setEntries] = useState<MonitoringEntry[] | null>(null);
  const [count, setCount] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageFetching, setPageFetching] = useState(false);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!liveMonitoring) {
      setEntries(null);
      setCount(0);
      setNewCount(0);
      setLoading(false);
      setPageFetching(false);
      setPage(1);
      hasLoadedRef.current = false;
      return;
    }
    if (!infoResolved) {
      setLoading(true);
      return;
    }
    let cancelled = false;
    if (hasLoadedRef.current) {
      setPageFetching(true);
    } else {
      setLoading(true);
    }
    Promise.all([listOpenAlerts(page), getNewMatchesPreview()]).then(
      ([alertsRes, newMatchesRes]) => {
        if (cancelled) return;
        setEntries(alertsRes.ok ? alertsRes.value.entries : []);
        setCount(alertsRes.ok ? alertsRes.value.count : 0);
        setNewCount(newMatchesRes.ok ? newMatchesRes.value.count : 0);
        setLoading(false);
        setPageFetching(false);
        hasLoadedRef.current = true;
      },
    );
    return () => {
      cancelled = true;
    };
  }, [liveMonitoring, infoResolved, page]);

  if (loading) return <TableCardSkeleton rows={skeletonRows} />;
  return (
    <ActiveMonitoringAlertsCard
      empty={empty}
      entries={entries}
      count={count}
      page={page}
      onPageChange={setPage}
      pageFetching={pageFetching}
      newCount={newCount}
    />
  );
}

function HeaderCell({ children }: { children: ReactNode }) {
  return (
    <span className="flex h-9 items-center whitespace-nowrap border-b border-grey-200 bg-grey-50 px-2 text-small font-medium text-grey-900">
      {children}
    </span>
  );
}

function AlertRow({ entry, gridTemplate }: { entry: MonitoringEntry; gridTemplate: string }) {
  return (
    <Link
      to={`/monitoring-portal/${entry.id}`}
      className={`grid bg-white hover:bg-grey-50 ${ROW_BORDER}`}
      style={{ gridTemplateColumns: gridTemplate }}
    >
      {ALERT_COLUMNS.map((col) => (
        <Fragment key={col.id}>{col.cell(entry)}</Fragment>
      ))}
    </Link>
  );
}
